<template>
    <div class="container-fluid">
        <div class="row">
            <div class="col-12">
                <div class="card">
                    <div class="card-header d-flex justify-content-between">
                        <h3 class="card-title">{{ $t('brands.title') }}</h3>
                        <div class="card-tools">
                            <button class="btn btn-primary" @click="openCreateModal">
                                <i class="fas fa-plus"></i> {{ $t('brands.add_brand') }}
                            </button>
                        </div>
                    </div>
                    <div class="card-body">
                        <div class="table-responsive">
                            <table class="table table-bordered table-striped">
                                <thead>
                                <tr>
                                    <th>{{ $t('brands.table_header_logo') }}</th>
                                    <th>{{ $t('brands.table_header_name') }}</th>
                                    <th>{{ $t('brands.table_header_description') }}</th>
                                    <th>{{ $t('brands.table_header_created_at') }}</th>
                                    <th>{{ $t('brands.table_header_actions') }}</th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr v-for="brand in brands" :key="brand.id">
                                    <td>
                                        <img
                                            v-if="brand.logo"
                                            :src="brand.logo"
                                            :alt="brand.name"
                                            class="img-thumbnail"
                                            style="max-width: 100px;"
                                        />
                                        <span v-else>{{ $t('brands.no_logo') }}</span>
                                    </td>
                                    <td>{{ brand.name }}</td>
                                    <td>{{ brand.description }}</td>
                                    <td>{{ brand.created_at }}</td>
                                    <td>
                                        <button class="btn btn-sm btn-info me-2" @click="editBrand(brand)">
                                            <i class="fas fa-edit"></i>
                                        </button>
                                        <button class="btn btn-sm btn-danger" @click="deleteBrand(brand)">
                                            <i class="fas fa-trash"></i>
                                        </button>
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Create/Edit Modal -->
        <b-modal
            v-model="showBrandModal"
            :title="isEditing ? $t('brands.edit_brand') : $t('brands.add_brand')"
            @hide="resetForm"
        >
            <form @submit.prevent="submitForm">
                <div class="mb-3">
                    <label class="form-label">{{ $t('brands.table_header_name') }}</label>
                    <input
                        type="text"
                        class="form-control"
                        v-model="form.name"
                        @input="removeError('name')"
                        required
                    >
                    <p class="text-danger" v-if="formErrors.has('name')">
                        {{ formErrors.first('name') }}
                    </p>
                </div>
                <div class="mb-3">
                    <label class="form-label">{{ $t('brands.table_header_description') }}</label>
                    <textarea
                        class="form-control"
                        v-model="form.description"
                        @input="removeError('description')"
                        rows="3"
                    ></textarea>
                    <p class="text-danger" v-if="formErrors.has('description')">
                        {{ formErrors.first('description') }}
                    </p>
                </div>
                <div class="mb-3">
                    <label class="form-label">{{ $t('brands.table_header_logo') }}</label>
                    <input
                        type="file"
                        class="form-control"
                        @change="handleFileUpload"
                        @input="removeError('logo')"
                        accept="image/*"
                    >
                    <div v-if="form.logoPreview" class="mt-2">
                        <img
                            :src="form.logoPreview"
                            class="img-thumbnail"
                            style="max-width: 200px;"
                        />
                    </div>
                    <p class="text-danger" v-if="formErrors.has('logo')">
                        {{ formErrors.first('logo') }}
                    </p>
                </div>
            </form>
            <template #modal-footer>
                <b-button variant="secondary" @click="showBrandModal = false">
                    {{ $t('common.cancel') }}
                </b-button>
                <b-button variant="primary" @click="submitForm">
                    {{ $t('common.save') }}
                </b-button>
            </template>
        </b-modal>

        <!-- Delete Confirmation Modal -->
        <b-modal
            v-model="showDeleteModal"
            :title="$t('brands.delete_modal_title')"
        >
            <p>{{ $t('brands.delete_modal_message') }}</p>
            <template #modal-footer>
                <b-button variant="secondary" @click="showDeleteModal = false">
                    {{ $t('common.cancel') }}
                </b-button>
                <b-button variant="danger" @click="confirmDelete">
                    {{ $t('common.delete') }}
                </b-button>
            </template>
        </b-modal>
    </div>
</template>

<script>
import { ref, onMounted, reactive, getCurrentInstance } from 'vue'
import { request } from "@/Util/Request";
import NotifyMixin from "@/Util/NotifyMixin";
import Error from '@/Util/Error';
export default {
    name: 'BrandsList',
    mixins: [ NotifyMixin ],
    setup() {
        const brands = ref([])
        const form = ref({
            name: '',
            description: '',
            logo: null,
            logoPreview: null
        })
        const isEditing = ref(false)
        const currentBrandId = ref(null)
        const showBrandModal = ref(false)
        const showDeleteModal = ref(false)
        const brandToDelete = ref(null)
        let formErrors	    =	 reactive(new Error({}));
        const { proxy } = getCurrentInstance();
        const loadBrands = async () => {
            try {
                const response = await request({
                    method: "get",
                    url:'admin/brands'});
                brands.value = response.data;
            } catch (error) {
                console.log('error',error)
            }
        }

        const openCreateModal = () => {
            isEditing.value = false
            form.value = {
                name: '',
                description: '',
                logo: null,
                logoPreview: null
            }
            showBrandModal.value = true
        }

        const editBrand = async (brand) => {
            try {
                isEditing.value = true;
                currentBrandId.value = brand.id;

                // Fetch the brand details to ensure we have the latest data
                const response = await request({
                    method: "get",
                    url: `admin/brands/${brand.id}`
                });

                form.value = {
                    name: response.data.name,
                    description: response.data.description || '',
                    logo: null,
                    logoPreview: response.data.logo || null
                };

                showBrandModal.value = true;
            } catch (error) {
                console.error('Error fetching brand details:', error);
            }
        }

        const resetForm = () => {
            form.value = {
                name: '',
                description: '',
                logo: null,
                logoPreview: null
            }
            isEditing.value = false
            currentBrandId.value = null
        }

        const handleFileUpload = (event) => {
            const file = event.target.files[0];
            if (file) {
                form.value.logo = file;
                form.value.logoPreview = URL.createObjectURL(file);
            }
        }

        const submitForm = async () => {
            try {
                const formData = new FormData();
                formData.append('name', form.value.name);
                formData.append('description', form.value.description);

                // Only append logo if a new file is selected
                if (form.value.logo instanceof File) {
                    formData.append('logo', form.value.logo);
                }

                // Add _method for Laravel to handle PUT request
                if (isEditing.value) {
                    formData.append('_method', 'PUT');
                }

                const url = isEditing.value
                    ? `admin/brands/${currentBrandId.value}`
                    : 'admin/brands';

                    try {
                        await request({
                            method: "post", // Always use POST, _method will handle PUT
                            url: url,
                            data: formData,
                            headers: {
                                'Content-Type': 'multipart/form-data',
                                'Accept': 'application/json'
                            }
                        });
                        showBrandModal.value = false;
                        const successMessage = isEditing.value 
                        ? proxy.$t("brands.updated_successfully") 
                        : proxy.$t("brands.created_successfully");
                        proxy.notifySuccessWithMsg(successMessage);
                        Object.assign(formErrors, new Error({}));
                        loadBrands();
                    } catch (err) {
                        if(err?.data?.errors && err.status == 422){
                            Object.assign(formErrors, new Error(err.data.errors));
                        }else{
                            let error = err.data?.message || proxy.$t("brands.save_error");
                            proxy.notifyError(error);
                        }
                    }

            } catch (error) {
                console.error('Error saving brand:', error);
                if (error.response?.data?.errors) {
                    // Handle validation errors
                    const errors = error.response.data.errors;
                    console.log(errors);
                }
            }
        }

        const deleteBrand = (brand) => {
            brandToDelete.value = brand;
            showDeleteModal.value = true;
        }

        const confirmDelete = async () => {
            if (!brandToDelete.value) return;

            try {
                await request({
                    method: "delete",
                    url: `admin/brands/${brandToDelete.value.id}`
                });
                showDeleteModal.value = false;
                loadBrands();
            } catch (error) {
                console.error('Error deleting brand:', error);
            }
        }

        const formatDate = (date) => {
            return new Date(date).toLocaleDateString()
        }

        onMounted(() => {
            loadBrands();
        });
        const removeError   =   (target) => {
            if (typeof target === 'object') {
                for (let i = 0; i < target.length; i++) {
                    formErrors.remove(target[i]);
                }
            } else {
                formErrors.remove(target);
            }
        }

        return {
            brands,
            form,
            isEditing,
            showBrandModal,
            showDeleteModal,
            openCreateModal,
            editBrand,
            deleteBrand,
            confirmDelete,
            handleFileUpload,
            submitForm,
            resetForm,
            formatDate,
            formErrors,
            removeError
        }
    }
}
</script>

<style scoped>
.table img {
    max-width: 100px;
    height: auto;
}
</style>
